// ─── unlockCache: "Remember for 12h" on this device ───
// Opening a link (new tab / page load) wipes the in-memory unlocked key, so
// the wallet came back locked. This keeps the UNLOCKED secret for 12h,
// encrypted with a NON-EXTRACTABLE AES-GCM CryptoKey stored in IndexedDB.
// The raw key/password is never written in plaintext; script on this origin
// can use the CryptoKey to decrypt but cannot export it.
// Cleared on lock, remove, expiry, or when "Remember" is turned off.

export const REMEMBER_MS = 12 * 60 * 60 * 1000   // 12 hours
const PREF_KEY = 'cheddar_remember_unlock'       // '0' = off, anything else = on (default on)
const DB_NAME = 'cheddar_unlock'
const STORE = 'k'

export function getRememberPref() {
  try { return localStorage.getItem(PREF_KEY) !== '0' } catch { return true }
}

export function setRememberPref(on) {
  try { localStorage.setItem(PREF_KEY, on ? '1' : '0') } catch {}
  if (!on) { forget('sol'); forget('hl') }
}

let _db = null
function db() {
  if (_db) return _db
  _db = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB unavailable'))
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  _db.catch(() => { _db = null })
  return _db
}

function tx(mode, fn) {
  return db().then(d => new Promise((resolve, reject) => {
    const t = d.transaction(STORE, mode)
    const r = fn(t.objectStore(STORE))
    t.oncomplete = () => resolve(r && r.result)
    t.onerror = () => reject(t.error)
    t.onabort = () => reject(t.error)
  }))
}

// Remember a UTF-8 string secret under `name` for REMEMBER_MS. No-op if the
// user turned Remember off. Never throws (best-effort convenience).
export async function remember(name, text) {
  try {
    if (!getRememberPref()) return
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(text))
    await tx('readwrite', s => s.put({ key, iv, ct, exp: Date.now() + REMEMBER_MS }, name))
  } catch {}
}

// Returns the remembered string, or null if none / expired / unreadable
// (in which case the entry is deleted).
export async function restore(name) {
  try {
    if (!getRememberPref()) { await forget(name); return null }
    const rec = await tx('readonly', s => s.get(name))
    if (!rec) return null
    if (!(rec.exp > Date.now())) { await forget(name); return null }
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: rec.iv }, rec.key, rec.ct)
    return new TextDecoder().decode(pt)
  } catch {
    await forget(name)
    return null
  }
}

export async function forget(name) {
  try { await tx('readwrite', s => s.delete(name)) } catch {}
}
